import React, { useEffect, useMemo, useRef } from 'react';
import {
    Box,
    Button,
    CircularProgress,
    FormControlLabel,
    MenuItem,
    Switch,
    TextField,
    Typography,
} from '@material-ui/core';
import { Form, Formik, useFormikContext } from 'formik';
import * as yup from 'yup';
import i18n from 'i18n-js';
import html2canvas from 'html2canvas';
import { useDispatch } from 'react-redux';
import { useAppSelector } from 'index';
import { images } from 'assets';
import { DayKey } from 'components/restaurantInformation/components/types';
import { FormikTimePicker } from 'components/shared';
import { parseTimeToMinutes } from 'helpers/orderInAdvanceHelper';
import restaurantStore from 'store/restaurantDetailsStore';
import { ReservationModule, RestaurantDetails } from 'store/restaurantDetailsStore/reducer';
import globalStore from 'store/globalStore';

interface ReservationSettingsFormValues {
    isOnlineAvailable: boolean;
    isSameDayAvailable: boolean;
    bookingHorizon: number | '';
    maxGuestsOnline: number | '';
    maxReservationDurationMinutes: number;
    sameDayUntilTime: number | null;
    sameDayUntilTimeInputInvalid: boolean;
}

interface Props {
    canEdit: boolean;
    onDirtyChange: (dirty: boolean) => void;
    onSavingChange: (saving: boolean) => void;
    submitFormRef: React.MutableRefObject<() => void>;
    resetFormRef: React.MutableRefObject<() => void>;
}

const dayKeys: DayKey[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
];

function getOpeningIntervals(restaurant: RestaurantDetails): Array<[number, number]> {
    const workingHours = restaurant?.dineIn?.workingHours;
    if (!workingHours) return [];

    return dayKeys.reduce<Array<[number, number]>>((intervals, day) => {
        (workingHours[day] || []).forEach(
            (hours: { from: number | string; to: number | string }) => {
                const from = parseTimeToMinutes(hours.from);
                const to = parseTimeToMinutes(hours.to);
                if (from === null || to === null) return;
                if (from === 0 && to === 0) {
                    intervals.push([0, 24 * 60]);
                } else if (from <= to) {
                    intervals.push([from, to]);
                } else {
                    intervals.push([from, 24 * 60], [0, to]);
                }
            }
        );
        return intervals;
    }, []);
}

function getInitialValues(module: ReservationModule): ReservationSettingsFormValues {
    const hasSavedSettings =
        (module.bookingHorizon ?? 0) > 0 ||
        (module.maxGuestsOnline ?? 0) > 0 ||
        module.maxReservationDurationMinutes > 0;

    return {
        isOnlineAvailable: module.isOnlineAvailable,
        isSameDayAvailable: hasSavedSettings ? module.isSameDayAvailable : false,
        bookingHorizon:
            module.bookingHorizon !== null && module.bookingHorizon > 0
                ? module.bookingHorizon
                : 60,
        maxGuestsOnline:
            module.maxGuestsOnline !== null && module.maxGuestsOnline > 0
                ? module.maxGuestsOnline
                : 8,
        maxReservationDurationMinutes: module.maxReservationDurationMinutes || 120,
        sameDayUntilTime:
            module.sameDayUntilTime > 0
                ? minutesToPickerValue(parseTimeToMinutes(module.sameDayUntilTime) ?? 0)
                : null,
        sameDayUntilTimeInputInvalid: false,
    };
}

function minutesToPickerValue(minutes: number): number {
    return Math.round((Math.floor(minutes / 60) + (minutes % 60) / 100) * 100) / 100;
}

function getDurationLabel(minutes: number): string {
    if (minutes < 60) return `${minutes} min`;
    if (minutes % 60 === 0) return `${minutes / 60} h`;
    return `${Math.floor(minutes / 60)} h ${minutes % 60} min`;
}

function FocusFirstError() {
    const { errors, submitCount } = useFormikContext<ReservationSettingsFormValues>();
    const fieldOrder: (keyof ReservationSettingsFormValues)[] = [
        'bookingHorizon',
        'maxGuestsOnline',
        'maxReservationDurationMinutes',
        'sameDayUntilTime',
    ];

    useEffect(() => {
        if (submitCount === 0) return;
        const firstError = fieldOrder.find((field) => errors[field]);
        if (firstError) document.getElementById(firstError)?.focus();
    }, [submitCount, errors]);

    return null;
}

function DirtyStateTracker({
    canEdit,
    onDirtyChange,
}: {
    canEdit: boolean;
    onDirtyChange: (dirty: boolean) => void;
}) {
    const { dirty } = useFormikContext<ReservationSettingsFormValues>();

    useEffect(() => {
        onDirtyChange(canEdit && dirty);
    }, [canEdit, dirty, onDirtyChange]);

    return null;
}

export default function ReservationSettings({
    canEdit,
    onDirtyChange,
    onSavingChange,
    submitFormRef,
    resetFormRef,
}: Props) {
    const dispatch = useDispatch();
    const restaurant = useAppSelector((state) => state.restaurantDetails.restaurantInformation);
    const module = restaurant?.reservationModule;
    const openingIntervals = useMemo(() => getOpeningIntervals(restaurant), [restaurant]);
    const qrDownloadRef = useRef<HTMLDivElement>(null);
    const requestUrl = `${(
        process.env.REACT_APP_WEBAPP_URL || 'https://dev.gastrojames.ch'
    ).replace(/\/+$/, '')}/restaurant/${restaurant?.id}/reservation`;
    const QRCode = require('qrcode.react');

    const validationSchema = useMemo(
        () =>
            yup.object().shape({
                bookingHorizon: yup
                    .number()
                    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
                    .typeError(i18n.t('restaurant.reservationSettings.bookingHorizonRangeError'))
                    .integer(i18n.t('restaurant.reservationSettings.bookingHorizonRangeError'))
                    .required(i18n.t('restaurant.reservationSettings.required'))
                    .min(1, i18n.t('restaurant.reservationSettings.bookingHorizonRangeError'))
                    .max(365, i18n.t('restaurant.reservationSettings.bookingHorizonRangeError')),
                maxGuestsOnline: yup
                    .number()
                    .transform((value, originalValue) => (originalValue === '' ? undefined : value))
                    .typeError(i18n.t('restaurant.reservationSettings.maxGuestsRangeError'))
                    .integer(i18n.t('restaurant.reservationSettings.maxGuestsRangeError'))
                    .required(i18n.t('restaurant.reservationSettings.required'))
                    .min(1, i18n.t('restaurant.reservationSettings.maxGuestsRangeError'))
                    .max(20, i18n.t('restaurant.reservationSettings.maxGuestsRangeError')),
                maxReservationDurationMinutes: yup
                    .number()
                    .required(i18n.t('restaurant.reservationSettings.required'))
                    .min(30, i18n.t('restaurant.reservationSettings.durationRangeError'))
                    .max(360, i18n.t('restaurant.reservationSettings.durationRangeError'))
                    .test(
                        '30-minute-increments',
                        i18n.t('restaurant.reservationSettings.durationRangeError'),
                        (value) => value !== undefined && value % 30 === 0
                    ),
                sameDayUntilTime: yup
                    .number()
                    .nullable()
                    .test(
                        'valid-time',
                        i18n.t('restaurant.reservationSettings.invalidTime'),
                        function (value) {
                            if (!this.parent.isOnlineAvailable || !this.parent.isSameDayAvailable) {
                                return true;
                            }
                            if (this.parent.sameDayUntilTimeInputInvalid) {
                                return false;
                            }
                            if (value === null || value === undefined) {
                                return true;
                            }
                            const minutes = parseTimeToMinutes(value);
                            return (
                                minutes !== null &&
                                Number.isInteger(minutes) &&
                                minutes >= 0 &&
                                minutes < 24 * 60
                            );
                        }
                    )
                    .test(
                        'within-opening-hours',
                        i18n.t('restaurant.reservationSettings.timeOutsideHours'),
                        function (value) {
                            if (
                                !this.parent.isOnlineAvailable ||
                                !this.parent.isSameDayAvailable ||
                                value === null ||
                                value === undefined
                            ) {
                                return true;
                            }
                            const minutes = parseTimeToMinutes(value);
                            if (minutes === null) return true;
                            return openingIntervals.some(
                                ([from, to]) => minutes >= from && minutes <= to
                            );
                        }
                    ),
            }),
        [openingIntervals, i18n.locale]
    );

    async function copyRequestLink() {
        try {
            if (navigator.clipboard && window.isSecureContext) {
                await navigator.clipboard.writeText(requestUrl);
            } else {
                const temporaryInput = document.createElement('textarea');
                temporaryInput.value = requestUrl;
                temporaryInput.style.position = 'fixed';
                temporaryInput.style.opacity = '0';
                document.body.appendChild(temporaryInput);
                temporaryInput.select();
                const copied = document.execCommand('copy');
                temporaryInput.remove();
                if (!copied) throw new Error('Clipboard copy is not supported');
            }
            globalStore.actionCreators.showToaster(
                'success',
                i18n.t('restaurant.reservationSettings.linkCopied')
            )(dispatch);
        } catch (error) {
            globalStore.actionCreators.showToaster('error', error)(dispatch);
        }
    }

    async function downloadQrCode() {
        const container = qrDownloadRef.current;
        if (!container) {
            globalStore.actionCreators.showToaster(
                'error',
                i18n.t('restaurant.reservationSettings.qrDownloadFailed')
            )(dispatch);
            return;
        }

        try {
            const canvas = await html2canvas(container, {
                scale: 2,
                onclone: (_document, element) => {
                    element.style.display = 'flex';
                },
            });
            const link = document.createElement('a');
            link.href = canvas.toDataURL('image/png');
            link.download = `${(restaurant.name || 'restaurant').replace(
                /[<>:"/\\|?*]/g,
                '-'
            )}-reservation-qr.png`;
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            globalStore.actionCreators.showToaster(
                'error',
                i18n.t('restaurant.reservationSettings.qrDownloadFailed')
            )(dispatch);
        }
    }

    if (!restaurant?.id || !module) {
        return (
            <Box display="flex" justifyContent="center" p={4}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Formik<ReservationSettingsFormValues>
            initialValues={getInitialValues(module)}
            validationSchema={validationSchema}
            enableReinitialize
            onSubmit={async (values, actions) => {
                if (!canEdit) return;

                onSavingChange(true);
                try {
                    const savedModule = await dispatch(
                        restaurantStore.actionCreators.updateReservationModule({
                            isOnlineAvailable: values.isOnlineAvailable,
                            isSameDayAvailable: values.isSameDayAvailable,
                            bookingHorizon: Number(values.bookingHorizon),
                            maxGuestsOnline: Number(values.maxGuestsOnline),
                            maxReservationDurationMinutes: values.maxReservationDurationMinutes,
                            sameDayUntilTime:
                                values.sameDayUntilTime === null
                                    ? 0
                                    : minutesToPickerValue(
                                          parseTimeToMinutes(values.sameDayUntilTime) ?? 0
                                      ),
                        })
                    );
                    actions.resetForm({
                        values: getInitialValues(savedModule as unknown as ReservationModule),
                    });
                } catch (_error) {
                    // The store action reports the error; leave the current values editable.
                } finally {
                    onSavingChange(false);
                }
            }}
        >
            {({
                values,
                errors,
                touched,
                handleBlur,
                handleChange,
                setFieldValue,
                submitForm,
                resetForm,
                isSubmitting,
            }) => {
                submitFormRef.current = submitForm;
                resetFormRef.current = () => resetForm();

                return (
                    <Form>
                        <FocusFirstError />
                        <DirtyStateTracker canEdit={canEdit} onDirtyChange={onDirtyChange} />

                        <Box px={2} pb={3}>
                            <Typography variant="caption" display="block" gutterBottom>
                                {i18n
                                    .t('restaurant.reservationSettings.onlineRequestsTitle')
                                    .toUpperCase()}
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={values.isOnlineAvailable}
                                        onChange={(_, checked) =>
                                            setFieldValue('isOnlineAvailable', checked)
                                        }
                                        color="primary"
                                        disabled={!canEdit || isSubmitting}
                                    />
                                }
                                label={i18n.t(
                                    'restaurant.reservationSettings.onlineRequestsToggle'
                                )}
                            />

                            <Box
                                display="flex"
                                alignItems="center"
                                mt={1}
                                style={{
                                    opacity: values.isOnlineAvailable ? 1 : 0.4,
                                    pointerEvents: values.isOnlineAvailable ? 'auto' : 'none',
                                }}
                            >
                                <Box
                                    border={1}
                                    borderColor="grey.300"
                                    borderRadius={4}
                                    p={1}
                                    mr={1}
                                    bgcolor="white"
                                >
                                    <div>
                                        <QRCode value={requestUrl} size={78} />
                                    </div>
                                </Box>
                                <Box display="flex" gridGap={12}>
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        color="primary"
                                        onClick={copyRequestLink}
                                        disabled={!values.isOnlineAvailable}
                                    >
                                        {i18n
                                            .t('restaurant.reservationSettings.copyLink')
                                            .toUpperCase()}
                                    </Button>
                                    <Button
                                        type="button"
                                        variant="outlined"
                                        color="primary"
                                        onClick={downloadQrCode}
                                        disabled={!values.isOnlineAvailable}
                                    >
                                        {i18n
                                            .t('restaurant.reservationSettings.downloadQr')
                                            .toUpperCase()}
                                    </Button>
                                </Box>
                            </Box>
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                                style={{ maxWidth: 560, marginTop: 10 }}
                            >
                                {i18n.t('restaurant.reservationSettings.shareDescription')}
                            </Typography>

                            <div
                                ref={qrDownloadRef}
                                aria-hidden="true"
                                style={{
                                    display: 'none',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 340,
                                    padding: '15px 2px',
                                    background:
                                        'linear-gradient(180deg, #ffd79a 9.9%, #ff9f9f 100%)',
                                }}
                            >
                                <span
                                    style={{
                                        width: 275,
                                        margin: '-5px 0 10px',
                                        overflowWrap: 'break-word',
                                        textAlign: 'center',
                                        fontWeight: 700,
                                    }}
                                >
                                    {restaurant.name}
                                </span>
                                <QRCode
                                    value={requestUrl}
                                    size={300}
                                    includeMargin
                                    level="Q"
                                    imageSettings={{
                                        src: images.icons.blackPenguin,
                                        width: 75,
                                        height: 75,
                                    }}
                                    style={{ borderRadius: 8 }}
                                />
                                <span
                                    style={{
                                        width: 275,
                                        margin: '6px 0 -5px',
                                        overflowWrap: 'break-word',
                                        textAlign: 'center',
                                        fontWeight: 700,
                                    }}
                                >
                                    {i18n.t('restaurant.reservationSettings.qrCaption')}
                                </span>
                            </div>

                            <Box mt={3}>
                                <Typography variant="caption" display="block" gutterBottom>
                                    {i18n
                                        .t('restaurant.reservationSettings.limitsTitle')
                                        .toUpperCase()}
                                </Typography>
                                <Box display="flex" flexWrap="wrap" gridGap={14}>
                                    <TextField
                                        id="bookingHorizon"
                                        name="bookingHorizon"
                                        type="number"
                                        variant="filled"
                                        label={i18n.t(
                                            'restaurant.reservationSettings.bookingHorizon'
                                        )}
                                        value={values.bookingHorizon}
                                        onChange={(event) =>
                                            setFieldValue(
                                                'bookingHorizon',
                                                event.target.value === ''
                                                    ? ''
                                                    : Number(event.target.value)
                                            )
                                        }
                                        onBlur={handleBlur}
                                        error={Boolean(
                                            touched.bookingHorizon && errors.bookingHorizon
                                        )}
                                        helperText={
                                            touched.bookingHorizon && errors.bookingHorizon
                                                ? errors.bookingHorizon
                                                : i18n.t(
                                                      'restaurant.reservationSettings.bookingHorizonHelper'
                                                  )
                                        }
                                        inputProps={{ min: 1, max: 365, step: 1 }}
                                        disabled={
                                            !canEdit || !values.isOnlineAvailable || isSubmitting
                                        }
                                        style={{ width: 280, maxWidth: '100%' }}
                                    />
                                    <TextField
                                        id="maxGuestsOnline"
                                        name="maxGuestsOnline"
                                        type="number"
                                        variant="filled"
                                        label={i18n.t('restaurant.reservationSettings.maxGuests')}
                                        value={values.maxGuestsOnline}
                                        onChange={(event) =>
                                            setFieldValue(
                                                'maxGuestsOnline',
                                                event.target.value === ''
                                                    ? ''
                                                    : Number(event.target.value)
                                            )
                                        }
                                        onBlur={handleBlur}
                                        error={Boolean(
                                            touched.maxGuestsOnline && errors.maxGuestsOnline
                                        )}
                                        helperText={
                                            touched.maxGuestsOnline && errors.maxGuestsOnline
                                                ? errors.maxGuestsOnline
                                                : i18n.t(
                                                      'restaurant.reservationSettings.maxGuestsHelper'
                                                  )
                                        }
                                        inputProps={{ min: 1, max: 20, step: 1 }}
                                        disabled={
                                            !canEdit || !values.isOnlineAvailable || isSubmitting
                                        }
                                        style={{ width: 250, maxWidth: '100%' }}
                                    />
                                    <TextField
                                        variant="filled"
                                        style={{ width: 800, maxWidth: '100%' }}
                                        InputProps={{
                                            style: { width: 280, maxWidth: '100%' },
                                        }}
                                        disabled={!canEdit || isSubmitting}
                                        id="maxReservationDurationMinutes"
                                        select
                                        name="maxReservationDurationMinutes"
                                        label={i18n.t(
                                            'restaurant.reservationSettings.maxReservationDuration'
                                        )}
                                        value={values.maxReservationDurationMinutes}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        error={Boolean(
                                            touched.maxReservationDurationMinutes &&
                                                errors.maxReservationDurationMinutes
                                        )}
                                        helperText={
                                            touched.maxReservationDurationMinutes &&
                                            errors.maxReservationDurationMinutes
                                                ? errors.maxReservationDurationMinutes
                                                : i18n.t(
                                                      'restaurant.reservationSettings.durationHelper'
                                                  )
                                        }
                                    >
                                        {Array.from({ length: 12 }, (_, index) => {
                                            const minutes = (index + 1) * 30;
                                            return (
                                                <MenuItem key={minutes} value={minutes}>
                                                    {getDurationLabel(minutes)}
                                                </MenuItem>
                                            );
                                        })}
                                    </TextField>
                                </Box>
                            </Box>

                            <Box mt={2}>
                                <Typography variant="caption" display="block" gutterBottom>
                                    {i18n
                                        .t('restaurant.reservationSettings.sameDayTitle')
                                        .toUpperCase()}
                                </Typography>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={values.isSameDayAvailable}
                                            onChange={(_, checked) =>
                                                setFieldValue('isSameDayAvailable', checked)
                                            }
                                            color="primary"
                                            disabled={
                                                !canEdit ||
                                                !values.isOnlineAvailable ||
                                                isSubmitting
                                            }
                                        />
                                    }
                                    label={i18n.t('restaurant.reservationSettings.sameDayToggle')}
                                />
                                <Box mt={1} style={{ width: 307, maxWidth: '100%' }}>
                                    <FormikTimePicker
                                        id="sameDayUntilTime"
                                        name="sameDayUntilTime"
                                        label={i18n.t(
                                            'restaurant.reservationSettings.sameDayCutoff'
                                        )}
                                        invalidDateMessage={i18n.t(
                                            'restaurant.reservationSettings.invalidTime'
                                        )}
                                        helperText={i18n.t(
                                            'restaurant.reservationSettings.sameDayCutoffHelper'
                                        )}
                                        showErrorBelow
                                        onInvalidInputChange={(isInvalid) =>
                                            setFieldValue('sameDayUntilTimeInputInvalid', isInvalid)
                                        }
                                        disabled={
                                            !canEdit ||
                                            !values.isOnlineAvailable ||
                                            !values.isSameDayAvailable ||
                                            isSubmitting
                                        }
                                        style={{ width: '100%' }}
                                    />
                                </Box>
                            </Box>

                            {!values.isOnlineAvailable && (
                                <Box
                                    mt={2}
                                    p={2}
                                    bgcolor="grey.100"
                                    borderRadius={4}
                                    maxWidth={620}
                                >
                                    <Typography variant="body2">
                                        {i18n.t('restaurant.reservationSettings.onlineRequestsOff')}
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Form>
                );
            }}
        </Formik>
    );
}
